<script lang="ts">import { cfgBrand, cfgBrandTitle, cfgHasSports, cfgHelpcenterUrl, cfgSupportEmail, cfgToThemeCdnPath, FeatureSet, ReleaseStage, stateAffiliatesEnabled, stateFeatureSet, stateUi, wantAtLeastReleaseStage } from "@infinitro/config";
import { t, Verbiage } from "@infinitro/content";
import { handleCustomerSupport, isFullScreenGame, isMobile, locale, loggedIn, stateShowGeoBlockFooter, stateShowSeal, stateShowSealText, toSiteUrl } from "@infinitro/global";
import { loadFooterLegalsHtml } from "./legals-content";
import AccordionSection from "./parts/accordion-section.svelte";
import CopyrightAlternative4 from "./parts/copyright-alternative4.svelte";
import Link from "./parts/link.svelte";
import Seal from "./parts/seal.svelte";
import SocialMedia from "./parts/social-media.svelte";
const BRAND_TITLE = cfgBrandTitle().toUpperCase();
const BRAND = cfgBrand();
const supportEmail = cfgSupportEmail();
const supportEmailStyles = "color: var(--color-primary-500);";
let htmlContent = $state("");
let useAlternative = $derived($stateUi.footer?.footerStyle === "alternative");
let useAlternative2 = $derived($stateUi.footer?.footerStyle === "alternative2");
let useAlternative4 = $derived($stateUi.footer?.footerStyle === "alternative4");
// Mirrors the display conditions below (legals markdown is only rendered for
// alternative2, or for alternative4 when logged in) so we don't fetch content
// that will never be shown.
let wantsLegalsContent = $derived(!!$stateUi.footer?.useLegalsMarkdownContent && $stateShowSealText && (useAlternative2 || useAlternative4 && $loggedIn));
// Guards against a stale response overwriting a newer one if the locale
// changes again before the previous fetch resolves.
let requestedLocale = "";
$effect(() => {
	if (!wantsLegalsContent) {
		htmlContent = "";
		return;
	}
	const targetLocale = $locale;
	requestedLocale = targetLocale;
	loadFooterLegalsHtml(targetLocale, BRAND_TITLE).then((html) => {
		if (requestedLocale === targetLocale) htmlContent = html;
	});
});
let useLiveChat = $derived($stateUi.footer?.useLiveChat ?? false);
let footerLinksCasinoSportsbookLayout = $derived($stateUi.footer?.footerLinksLayout === "with_casino_sportsbook");
let showFooterSports = $derived(cfgHasSports() && ($stateUi.footer?.showSports ?? true));
let useWideFooterMark = $derived(!!$stateUi.footer?.useWideFooterMark);
let useDefault = $derived($stateUi.footer?.footerStyle === "default");
let useEmbeddedShellDecor = $derived($stateUi.footer?.useEmbeddedShellDecor ?? false);
let hideFooterFaqLink = $derived($stateUi.footer?.hideFooterFaqLink ?? false);
let footerTextKey = $derived(useEmbeddedShellDecor ? Verbiage.footer_text_flush : Verbiage.footer_text);
let logoPath = $derived(cfgToThemeCdnPath(`/misc/logo.svg`));
let helpcenterUrl = $derived(cfgHelpcenterUrl());
let showAffiliatesLink = $derived($stateAffiliatesEnabled);
</script>

{#if !$isFullScreenGame}
	<nav
		id="footerId"
		class="footer"
		class:alternative4-footer={useAlternative4}
		class:alternative2-footer={useAlternative2}
		class:footer-links-casino-sportsbook={footerLinksCasinoSportsbookLayout}
	>
		<div class="footer-container">
			<div class="footer-content">
				<div class="footer-columns-wrapper">
					<!-- Logo column -->
					<div
						class="footer-column logo-column"
						class:alt2-logo-column={useAlternative2}
						class:footer-column-logo-alternative-4={useAlternative4}
					>
						<a
							href={toSiteUrl('/', $loggedIn)}
							class="logo"
							class:alt2-logo={useAlternative2}
							class:wide-brand-logo={useAlternative2 && useWideFooterMark}
							class:logo-a-alternative-4={useAlternative4}
						>
							<img src={logoPath} alt={$t(Verbiage.logo_alt, 'alts')} />
						</a>
					</div>
					{#if useAlternative4 && $isMobile}
						<div class="accordion-section-container">
							<AccordionSection />
						</div>
					{:else}
						<!-- Content columns -->
						<div class="content-columns">
							{#if footerLinksCasinoSportsbookLayout}
								<div class="footer-column" class:alt2-column={useAlternative2}>
									<h4 class="footer-title">{cfgBrandTitle()}</h4>
									<Link path="/vip/" verbiage={Verbiage.vip_galaxy} />
									<Link path="/about-us/" verbiage={Verbiage.about_us} />
									<Link
										path="/promotion-page/welcome-deposit-bonus/"
										verbiage={Verbiage.welcome_bonus}
									/>
									{#if showAffiliatesLink}
										<Link path="/affiliate/" verbiage={Verbiage.affiliates} />
									{/if}
									<button
										type="button"
										class="footer-link footer-link-plain-button"
										onclick={(e) => {
											e.preventDefault();
											handleCustomerSupport();
										}}
									>
										<span>{$t(Verbiage.help_center)}</span>
									</button>
								</div>

								<div class="footer-column" class:alt2-column={useAlternative2}>
									<h4 class="footer-title">{$t(Verbiage.casino)}</h4>
									<Link path="/originals/" verbiage={Verbiage.originals} />
									<Link path="/slots/" verbiage={Verbiage.slots} />
									<Link path="/livecasino/" verbiage={Verbiage.live_casino} />
									<Link path="/gameshows/" verbiage={Verbiage.game_shows} />
								</div>

								{#if showFooterSports}
									<div class="footer-column" class:alt2-column={useAlternative2}>
										<h4 class="footer-title">{$t(Verbiage.sportsbook)}</h4>
										<Link path="/sports/live/" verbiage={Verbiage.live_bets} />
										<Link path="/sports/soccer-1/" verbiage={Verbiage.football} />
										<Link path="/sports/tennis-5/" verbiage={Verbiage.tennis} />
										<Link path="/sports/basketball-2/" verbiage={Verbiage.basketball} />
									</div>
								{/if}

								<div class="footer-column" class:alt2-column={useAlternative2}>
									<h4 class="footer-title">{$t(Verbiage.terms)}</h4>
									<Link path="/terms-of-service/" verbiage={Verbiage.terms_of_service} />
									<Link path="/bonus-terms/" verbiage={Verbiage.bonus_terms} />
									{#if showFooterSports}
										<Link path="/terms-sports/" verbiage={Verbiage.sports_terms} />
									{/if}
								</div>

								<div class="footer-column" class:alt2-column={useAlternative2}>
									<h4 class="footer-title">{$t(Verbiage.privacy)}</h4>
									<Link path="/payment-policy/" verbiage={Verbiage.payment_policy} />
									<Link path="/aml-policy/" verbiage={Verbiage.aml_policy} />
									<Link path="/privacy-policy/" verbiage={Verbiage.privacy_policy} />
									<Link path="/kyc-policy/" verbiage={Verbiage.kyc_policy} />
								</div>
							{:else}
								<!-- Support column -->
								<div class="footer-column" class:alt2-column={useAlternative2}>
									<h4 class="footer-title">{$t(Verbiage.support)}</h4>
									<!-- link livechat here -->
									{#if useLiveChat}
										<button
											type="button"
											class="footer-link"
											class:alternative-footer-link={useAlternative || useAlternative4}
											onclick={(e) => {
												e.preventDefault();
												handleCustomerSupport();
											}}
										>
											<span>{$t(Verbiage.helpdesk)}</span>
										</button>
									{:else}
										<a
											href={helpcenterUrl}
											{...helpcenterUrl.startsWith('/') ? { 'data-sveltekit-reload': true } : {}}
											class="footer-link"
											class:alternative-footer-link={useAlternative || useAlternative4}
										>
											<span>{$t(Verbiage.helpdesk)}</span>
										</a>
									{/if}
									{#if !hideFooterFaqLink}
										<Link path="/faq/" verbiage={Verbiage.frequently_asked_questions} />
									{/if}
									<Link path="/responsible-gaming/" verbiage={Verbiage.responsible_gaming} />
									<a
										href="https://www.gamcare.org.uk/"
										target="_blank"
										class="footer-link"
										class:alternative-footer-link={useAlternative || useAlternative4}
									>
										<span>{$t(Verbiage.gamble_aware)}</span>
									</a>
								</div>

								<!-- Platform column -->
								<div class="footer-column" class:alt2-column={useAlternative2}>
									<h4 class="footer-title">{$t(Verbiage.platform)}</h4>
									<Link path="/about-us/" verbiage={Verbiage.about_us} />
									<Link path="/fairness/" verbiage={Verbiage.provably_fair} />
									{#if showAffiliatesLink}
										<Link path="/affiliate/" verbiage={Verbiage.affiliates} />
									{/if}
								</div>

								<!-- Terms column -->
								<div class="footer-column" class:alt2-column={useAlternative2}>
									<h4 class="footer-title">{$t(Verbiage.terms)}</h4>
									<Link path="/terms-of-service/" verbiage={Verbiage.terms_of_service} />
									<Link path="/bonus-terms/" verbiage={Verbiage.bonus_terms} />
									{#if showFooterSports}
										<Link path="/terms-sports/" verbiage={Verbiage.sports_terms} />
									{/if}
									<Link
										path="/complaints-and-arbitration/"
										verbiage={Verbiage.complaints_and_arbitration}
									/>
								</div>

								<!-- Privacy column -->
								<div class="footer-column" class:alt2-column={useAlternative2}>
									<h4 class="footer-title">{$t(Verbiage.privacy)}</h4>
									<Link path="/payment-policy/" verbiage={Verbiage.payment_policy} />
									<Link path="/aml-policy/" verbiage={Verbiage.aml_policy} />
									<Link path="/privacy-policy/" verbiage={Verbiage.privacy_policy} />
									{#if $stateFeatureSet === FeatureSet.Preview}
										<Link path="/kyc-policy/" verbiage={Verbiage.kyc_policy} />
									{/if}
								</div>
							{/if}
						</div>
					{/if}
				</div>
				{#if !useAlternative && !useAlternative2 && !useAlternative4 && $stateShowSealText}
					<div class="separator"></div>
					<p class="footer-text" class:alternative-footer-text={useAlternative || useAlternative4}>
						{$t(footerTextKey)}
					</p>
				{/if}
				{#if useAlternative2 && $stateShowSealText}
					<div class="footer-text footer-markdown" class:alternative2-footer-text={useAlternative2}>
						{@html htmlContent}
					</div>
				{/if}
				{#if $stateShowGeoBlockFooter && !$loggedIn}
					<div class="separator"></div>
					<p class="footer-text" class:alternative-footer-text={useAlternative || useAlternative4}>
						{@html $t(Verbiage.sorry_text, 'global', { BRAND })}

						{#if $isMobile}
							{@html $t(Verbiage.gaming_license_norms_text_mobile, 'global', {
								BRAND,
								SUPPORT_EMAIL: supportEmail,
								SUPPORT_EMAIL_STYLES: supportEmailStyles
							})}
						{:else}
							{@html $t(Verbiage.gaming_license_norms_text, 'global', {
								BRAND,
								SUPPORT_EMAIL: supportEmail,
								SUPPORT_EMAIL_STYLES: supportEmailStyles
							})}
						{/if}
					</p>
				{/if}
				{#if !(useAlternative2 || useAlternative4)}
					<div class="separator"></div>
				{/if}

				{#if useAlternative4}
					<CopyrightAlternative4 />
				{:else}
					<div class="footer-bottom">
						<div class="copyright">
							<p
								class="footer-text"
								class:alternative-footer-text={useAlternative || useAlternative4}
								class:alternative2-footer-text={useAlternative2}
							>
								&copy;{new Date().getFullYear()}
								{cfgBrandTitle()}
								{$t(Verbiage.all_rights_reserved)}
							</p>
						</div>
						<div
							class="seal-logos-container"
							class:alternative-seal-logos-container={useAlternative || useAlternative4}
						>
							{#if $stateShowSeal}
								<Seal />
							{/if}
							{#if $stateFeatureSet === FeatureSet.Preview && wantAtLeastReleaseStage(ReleaseStage.Alpha) && !$stateShowSeal}
								<img
									src={cfgToThemeCdnPath(`/footer/placeholder.svg`)}
									alt={$t(Verbiage.logo_alt, 'alts')}
									class="footer-logo"
								/>
							{/if}

							{#if useDefault}
								<a
									href="https://responsiblegambling.org/"
									target="_blank"
									rel="noopener noreferrer"
								>
									<img
										src={cfgToThemeCdnPath(`/footer/responsible-gaming.svg`)}
										alt={$t(Verbiage.logo_alt, 'alts')}
										class="footer-logo"
									/>
								</a>

								<a href="https://www.gamcare.org.uk/" target="_blank" rel="noopener noreferrer">
									<img
										src={cfgToThemeCdnPath(`/footer/gamcare.svg`)}
										alt={$t(Verbiage.logo_alt, 'alts')}
										class="footer-logo"
									/>
								</a>

								<a href={`${toSiteUrl('/terms-of-service/', $loggedIn)}#point-18`}>
									<img
										src={cfgToThemeCdnPath(`/footer/18+.svg`)}
										alt={$t(Verbiage.logo_alt, 'alts')}
										class="footer-logo"
									/>
								</a>
							{/if}
						</div>
						<div class="social-media">
							<SocialMedia />
						</div>
					</div>
				{/if}
				{#if useAlternative4 && $loggedIn && $stateShowSealText}
					<div class="footer-text footer-markdown" class:alternative2-footer-text={useAlternative2}>
						{@html htmlContent}
					</div>
				{/if}
			</div>
		</div>
	</nav>
{/if}

<style>
	.footer {
		z-index: 40;
		width: 100%;
		border-top-width: 1px;
		border-color: var(--color-neutral-900);
		background-color: var(--color-background-700);
		--tw-bg-opacity: 1;
		padding-left: 22px;
		padding-right: 22px;
		--tw-backdrop-blur: blur(50px);
		backdrop-filter: var(--tw-backdrop-blur) var(--tw-backdrop-brightness) var(--tw-backdrop-contrast) var(--tw-backdrop-grayscale) var(--tw-backdrop-hue-rotate) var(--tw-backdrop-invert) var(--tw-backdrop-opacity) var(--tw-backdrop-saturate) var(--tw-backdrop-sepia);
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
		font-family: var(--font-main);
		display: flex;
		align-items: center;
		justify-content: center;
}

	.alternative4-footer {
		border-width: 0px;
		background-color: var(--component-layout-side-bar-bg);
}

	.alternative2-footer {
		border-style: none;
		background: radial-gradient(
			100% 250% at 50% -150%,
			rgba(var(--color-primary-500-rgb) / 0.8) 0%,
			var(--color-background-900) 100%
		);
}

	.footer-container {
		margin-left: auto;
		margin-right: auto;
		margin-bottom: 70px;
		margin-top: 28px;
		display: flex;
		width: 100%;
		max-width: 1136px;
		flex-direction: column;
		gap: 1.25rem;
}

	.footer-content {
		display: flex;
		width: 100%;
		flex-direction: column;
		container-type: inline-size;
}

	/* Main columns wrapper */
	.footer-columns-wrapper {
		display: flex;
		flex-wrap: wrap;
		width: 100%;
		gap: 1rem;
	}

	/* Logo column */
	.logo-column {
		max-width: 150px;
		flex: 0 0 auto;
	}

	.alt2-logo-column {
		max-width: 300px;
	}

	/* Content columns */
	.content-columns {
		display: grid;
		grid-template-columns: repeat(4, 1fr);
		grid-gap: 1rem;
		flex: 1;
	}

	.alternative2-footer .content-columns {
		grid-template-columns: repeat(4, max-content);
		justify-items: start;
		justify-content: space-between;
	}

	.alternative2-footer.footer-links-casino-sportsbook .content-columns {
		grid-template-columns: repeat(5, max-content);
	}

	.footer-link-plain-button {
		cursor: pointer;
		-webkit-appearance: none;
		   -moz-appearance: none;
		        appearance: none;
		border-width: 0px;
		background-color: transparent;
		padding: 0px;
		text-align: left;
		font-weight: 400;
}

	.alternative2-footer .footer-column.alt2-column {
		width: -moz-max-content;
		width: max-content;
	}

	.alternative2-footer .footer-link,
	.alternative2-footer .alternative-footer-link {
		display: inline-block;
		width: -moz-max-content;
		width: max-content;
	}

	/* Responsive layout adjustments */
	@container (max-width: 900px) {
		.footer-columns-wrapper {
			flex-direction: column;
		}

		.logo-column {
			width: 100%;
			margin-bottom: 1rem;
		}

		.content-columns {
			grid-template-columns: repeat(2, 1fr);
		}

		/* Ensure text wraps properly in narrow columns */
		.footer-column {
			word-wrap: break-word;
			overflow-wrap: break-word;
			hyphens: auto;
		}

		.footer-link,
		.alternative-footer-link {
			max-width: 100%;
		}
	}

	/* Removed the 1-column layout for mobile - maintaining 2 columns */

	.footer-column {
		margin-bottom: 1.25rem;
		display: flex;
		flex-direction: column;
		justify-content: flex-start;
}

	.logo {
		margin-bottom: 1.25rem;
		margin-right: 1.25rem;
		width: 100px;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}

	.alt2-logo {
		width: 300px;
}

	.wide-brand-logo img {
		height: 26px;
		width: auto;
		max-width: 100%;
	}

	.footer-title {
		margin-bottom: 0.5rem;
		font-size: 0.875rem;
		line-height: 1.25rem;
		font-weight: 700;
		text-transform: uppercase;
		line-height: 14px;
		letter-spacing: 0.1em;
		--tw-text-opacity: 1;
		color: rgb(255 255 255 / var(--tw-text-opacity, 1));
}

	.footer-link,
	.alternative-footer-link {
		margin-bottom: 0.375rem;
		cursor: pointer;
		text-align: start;
		font-size: 0.75rem;
		line-height: 1rem;
		line-height: 16.8px;
		color: var(--color-text-300);
		font-family: var(--font-main);
}

	.footer-link:hover,
	.alternative-footer-link:hover {
		color: var(--color-text-100);
}

	.alternative-footer-link {
		color: var(--color-text-500);
}

	.footer-link:nth-child(n),
	.alternative-footer-link:nth-child(n) {
		margin-top: 0.375rem;
}

	.footer-column-logo-alternative-4 {
		margin-bottom: 0px;
}

	@media (min-width: 1024px) {

		.footer-column-logo-alternative-4 {
				margin-bottom: 1.25rem;
		}
}

	.logo-a-alternative-4 {
		margin-bottom: 0px;
}

	@media (min-width: 1024px) {

		.logo-a-alternative-4 {
				margin-bottom: 1.25rem;
		}
}

	.separator {
		margin-top: 0.5rem;
		margin-bottom: 0.5rem;
		height: 1px;
		width: 100%;
		background-color: var(--color-neutral-900);
}

	.footer-text,
	.alternative-footer-text {
		margin-top: 0.5rem;
		margin-bottom: 0.5rem;
		font-size: 0.75rem;
		line-height: 1rem;
		color: var(--color-text-300);
		font-family: var(--font-main);
}

	.alternative-footer-text {
		color: var(--color-text-300) !important;
}

	.alternative2-footer-text {
		color: var(--color-text-100);
}

	.footer-bottom {
		display: flex;
		flex-direction: column;
		align-items: center;
		width: 100%;
	}

	.footer-bottom .social-media {
		order: 1;
	}

	.footer-bottom .copyright {
		order: 2;
	}

	.footer-bottom .seal-logos-container {
		order: 3;
	}

	.copyright {
		margin-bottom: 0.5rem;
		margin-top: 1rem;
		text-align: center;
}

	.copyright .footer-text {
		font-size: 12px !important;
	}

	.copyright .alternative2-footer-text {
		color: var(--color-text-100) !important;
	}

	.social-media {
		margin-bottom: 0.5rem;
		margin-top: 0.5rem;
}

	.seal-logos-container {
		margin-top: 1rem;
		margin-bottom: 1.25rem;
		display: flex;
		width: 100%;
		align-items: center;
		justify-content: space-between;
		gap: 0.5rem;
}

	.alternative-seal-logos-container {
		justify-content: center;
}

	.footer-logo {
		height: 30px;
		width: auto;
}

	/* Responsive adjustments for footer bottom */
	@container (min-width: 600px) {
		.footer-link,
		.alternative-footer-link {
				font-size: 0.875rem;
				line-height: 1.25rem;
		}

		.footer-text,
		.alternative-footer-text {
				font-size: 0.875rem;
				line-height: 1.25rem;
		}

		.footer-bottom {
			flex-direction: row;
			justify-content: space-between;
			align-items: center;
		}

		.copyright {
				margin-bottom: 0px;
				margin-top: 0px;
				text-align: left;
		}

		.social-media {
				margin-top: 0px;
		}

		.seal-logos-container {
				justify-content: center;
				gap: 1.5rem;
			width: auto;
			flex: 1;
		}

		.footer-logo {
				height: 35px;
				width: auto;
		}

		.footer-bottom .social-media,
		.footer-bottom .copyright,
		.footer-bottom .seal-logos-container {
			order: initial;
		}

		.copyright {
			flex: 0 0 auto;
		}
	}

	.accordion-section-container {
		display: flex;
		width: 100%;
		flex-direction: column;
		max-width: 1232px;
}

	.none {
		display: none;
}

	.tobique-seal-placeholder {
		height: 50px;
		width: 40px;
}

	@media (min-width: 1024px) {
		.footer-container {
				margin-bottom: 2rem;
		}
	}
</style>
